import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, Clock, CheckCircle2, AlertCircle, User, Users, 
  Calendar, Tag, Paperclip, Mic, MicOff, Send, X, 
  ChevronDown, ChevronUp, FileText, Image, Download,
  Play, Pause, Check, XCircle, Edit2, Trash2, Filter,
  FileSpreadsheet, Sparkles, Camera, ListTree
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import { API, BACKEND_URL, getApiUrl } from '../utils/api';
import { fetchWithRetry } from '../utils/axiosClient';
import PDFImportModal from './PDFImportModal';

// Helper: Korrigiert Datei-URLs für R2 und lokale Uploads
const getFullFileUrl = (filePath) => {
  if (!filePath) return '';
  
  // Wenn file_path ein Objekt ist (alte R2-Antwort), verwende public_url
  if (typeof filePath === 'object' && filePath.public_url) {
    return filePath.public_url;
  }
  
  // R2 URLs (https://...) direkt zurückgeben
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  
  // Lokale URLs mit Backend-URL kombinieren
  if (filePath.startsWith('/api/')) {
    return `${BACKEND_URL}${filePath}`;
  }
  
  // Relative URLs
  if (filePath.startsWith('/')) {
    return `${BACKEND_URL}/api${filePath}`;
  }
  
  return filePath;
};

// Status badge colors
const statusColors = {
  open: 'bg-blue-100 text-blue-800',
  assigned: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-purple-100 text-purple-800',
  review: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  documented: 'bg-teal-100 text-teal-800'
};

// Status labels will be provided via translation
const getStatusLabel = (status, t) => {
  const labels = {
    open: t('tasks.statusOpen'),
    assigned: t('tasks.statusAssigned'),
    in_progress: t('tasks.statusInProgress'),
    review: t('tasks.statusReview'),
    completed: t('tasks.statusCompleted'),
    rejected: t('tasks.statusRejected'),
    documented: t('tasks.statusDocumented')
  };
  return labels[status] || status;
};

const priorityColors = {
  normal: 'bg-gray-100 text-gray-700',
  urgent: 'bg-red-100 text-red-700'
};

// Compare tasks by LV import-file order
const lvCompare = (a, b) => {
  if (a.lv_order != null && b.lv_order != null) return a.lv_order - b.lv_order;
  const parsePos = (p) => String(p || '').split('.').map(n => parseInt(n, 10) || 0);
  const pa = parsePos(a.lv_position);
  const pb = parsePos(b.lv_position);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
};

// Task List Component
export const TaskList = ({ projectId, projectName, currentUser, projectMembers = [] }) => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuickDocModal, setShowQuickDocModal] = useState(false);
  const [showPDFImportModal, setShowPDFImportModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [showReportGenerator, setShowReportGenerator] = useState(false);

  useEffect(() => {
    loadTasks();
  }, [projectId]);

  const loadTasks = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tasks/project/${projectId}`);
      const data = await response.json();
      setTasks(data.tasks || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'my') return task.assigned_to === currentUser.id || task.claimed_by === currentUser.id || task.created_by === currentUser.id;
    if (filter === 'open') return task.status === 'open';
    if (filter === 'active') return ['assigned', 'in_progress', 'review'].includes(task.status);
    if (filter === 'completed') return task.status === 'completed';
    if (filter === 'documented') return task.status === 'documented';
    return true;
  });

  const handleClaimTask = async (taskId) => {
    try {
      await fetch(`${getApiUrl()}/tasks/${taskId}/claim?user_id=${currentUser.id}`, { method: 'PUT' });
      loadTasks();
    } catch (error) {
      alert(t('alerts.taskClaimError'));
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm(t('tasks.confirmDelete', 'Möchten Sie diesen Task wirklich löschen?'))) {
      return;
    }
    
    try {
      const response = await fetch(`${getApiUrl()}/tasks/${taskId}`, { method: 'DELETE' });
      if (response.ok) {
        loadTasks();
      } else {
        alert(t('alerts.taskDeleteError', 'Task konnte nicht gelöscht werden'));
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      alert(t('alerts.taskDeleteError', 'Task konnte nicht gelöscht werden'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="p-4 bg-white border-b">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Tasks</h2>
            <span className="text-sm text-gray-500">({filteredTasks.length})</span>
            {/* Green buttons next to task count */}
            <Button 
              onClick={() => setShowCreateModal(true)}
              size="sm"
              className="h-8 bg-emerald-600 hover:bg-emerald-700 ml-2"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('tasks.newBtn', 'Neu')}
            </Button>
            <Button 
              onClick={() => setShowQuickDocModal(true)}
              size="sm"
              className="h-8 bg-emerald-600 hover:bg-emerald-700"
              title={t('tasks.quickDoc', 'Schnelle Dokumentation')}
            >
              <Paperclip className="w-4 h-4 mr-1" />
              {t('tasks.docBtn', 'Doku')}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowReportGenerator(!showReportGenerator)}
              className="h-8"
              title={t('tasks.reportTitle', 'Projektdokumentation erstellen')}
            >
              <FileText className="w-4 h-4 mr-1" />
              {t('tasks.report', 'Report')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="h-8"
            >
              <Filter className="w-4 h-4 mr-1" />
              {t('tasks.filterBtn', 'Filter')}
            </Button>
            <Button
              variant={sortBy === 'lv' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy(sortBy === 'lv' ? 'newest' : 'lv')}
              className={`h-8 ${sortBy === 'lv' ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}`}
              title={t('tasks.sortLv', 'Nach LV-Position')}
              data-testid="task-sort-lv-btn"
            >
              <ListTree className="w-4 h-4 mr-1" />
              LV
            </Button>
            <Button 
              onClick={() => setShowPDFImportModal(true)}
              size="sm"
              className="h-8 bg-purple-600 hover:bg-purple-700"
              title={t('pdfImport.buttonLabel', 'PDF importieren')}
              data-testid="pdf-import-btn"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Report Generator (collapsible) */}
      {showReportGenerator && (
        <div className="border-b bg-white">
          <ReportGeneratorInline projectId={projectId} projectName={projectName} />
        </div>
      )}

      {/* Filter Bar */}
      {showFilters && (
        <div className="p-3 bg-white border-b flex flex-wrap gap-2">
          {[
            { key: 'all', label: t('tasks.filter.all', 'Alle') },
            { key: 'my', label: t('tasks.filter.my', 'Meine') },
            { key: 'open', label: t('tasks.filter.open', 'Offen') },
            { key: 'active', label: t('tasks.filter.active', 'Aktiv') },
            { key: 'completed', label: t('tasks.filter.completed', 'Erledigt') },
            { key: 'documented', label: t('tasks.filter.documented', '📸 Doku') }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                filter === f.key 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Task List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredTasks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{t('tasks.noTasks', 'Keine Tasks vorhanden')}</p>
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('tasks.createFirstTask', 'Create first task')}
            </Button>
          </div>
        ) : sortBy === 'lv' ? (
          (() => {
            const groups = new Map();
            filteredTasks.forEach(task => {
              const cat = task.category || t('tasks.noCategory', 'Ohne Kategorie');
              if (!groups.has(cat)) groups.set(cat, []);
              groups.get(cat).push(task);
            });
            const groupArr = [...groups.entries()];
            groupArr.forEach(([, items]) => items.sort(lvCompare));
            const minOrder = (items) => Math.min(...items.map(x => x.lv_order ?? Number.MAX_SAFE_INTEGER));
            groupArr.sort((a, b) => minOrder(a[1]) - minOrder(b[1]));
            return groupArr.map(([cat, items]) => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-2 mt-1" data-testid="task-category-header">
                  <Tag className="w-4 h-4 text-indigo-500" />
                  <h3 className="text-sm font-semibold text-gray-700">{cat}</h3>
                  <span className="text-xs text-gray-400">({items.length})</span>
                  <div className="flex-1 border-t border-gray-200"></div>
                </div>
                <div className="space-y-3">
                  {items.map(task => (
                    <TaskCard 
                      key={task.id}
                      task={task}
                      currentUser={currentUser}
                      onClick={() => setSelectedTask(task)}
                      onClaim={() => handleClaimTask(task.id)}
                      onDelete={() => handleDeleteTask(task.id)}
                    />
                  ))}
                </div>
              </div>
            ));
          })()
        ) : (
          filteredTasks.map(task => (
            <TaskCard 
              key={task.id}
              task={task}
              currentUser={currentUser}
              onClick={() => setSelectedTask(task)}
              onClaim={() => handleClaimTask(task.id)}
              onDelete={() => handleDeleteTask(task.id)}
            />
          ))
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <CreateTaskModal
          projectId={projectId}
          currentUser={currentUser}
          projectMembers={projectMembers}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            loadTasks();
            setShowCreateModal(false);
          }}
        />
      )}

      {/* PDF Import Modal */}
      {showPDFImportModal && (
        <PDFImportModal
          isOpen={showPDFImportModal}
          currentUser={currentUser}
          selectedProject={{ id: projectId, name: projectName }}
          onClose={() => setShowPDFImportModal(false)}
          onImportComplete={() => {
            loadTasks();
            setShowPDFImportModal(false);
          }}
        />
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          currentUser={currentUser}
          projectMembers={projectMembers}
          onClose={() => setSelectedTask(null)}
          onUpdate={() => {
            loadTasks();
            setSelectedTask(null);
          }}
          onDelete={() => handleDeleteTask(selectedTask.id)}
        />
      )}

      {/* Quick Documentation Modal */}
      {showQuickDocModal && (
        <QuickDocModal
          projectId={projectId}
          currentUser={currentUser}
          onClose={() => setShowQuickDocModal(false)}
          onCreated={() => {
            loadTasks();
            setShowQuickDocModal(false);
          }}
        />
      )}
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, currentUser, onClick, onClaim, onDelete }) => {
  const { t } = useTranslation();
  const isAssignedToMe = task.assigned_to === currentUser.id || task.claimed_by === currentUser.id;
  const canClaim = task.status === 'open' && !task.assigned_to && task.created_by !== currentUser.id;
  // Only task creator can delete
  const canDelete = task.created_by === currentUser.id;
  
  // Check for LV special markers
  const isBedarfsposition = task.is_bedarfsposition || task.tags?.includes('Bedarfsposition');
  const isAlternativposition = task.is_alternativposition || task.tags?.includes('Alternativposition');

  // Card styling based on position type
  let cardClass = "bg-white rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer";
  if (isBedarfsposition) {
    cardClass = "bg-orange-50 rounded-lg border border-orange-300 border-l-4 p-4 hover:shadow-md transition-shadow cursor-pointer";
  } else if (isAlternativposition) {
    cardClass = "bg-yellow-50 rounded-lg border border-yellow-300 border-l-4 p-4 hover:shadow-md transition-shadow cursor-pointer";
  }

  return (
    <div 
      className={cardClass}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[task.status]}`}>
              {getStatusLabel(task.status, t)}
            </span>
            {task.priority === 'urgent' && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors.urgent}`}>
                ⚠️ {t('tasks.high')}
              </span>
            )}
            {isBedarfsposition && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-200 text-orange-800">
                ⚡ Bedarf
              </span>
            )}
            {isAlternativposition && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800">
                🔄 Alternativ
              </span>
            )}
            {task.category && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 flex items-center gap-1 max-w-[160px]" data-testid="task-category-badge">
                <Tag className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{task.category}</span>
              </span>
            )}
          </div>
          <h3 className="font-medium text-gray-900 truncate">{task.title}</h3>
          {task.description && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{task.description}</p>
          )}
        </div>
        {isAssignedToMe && (
          <Avatar className="w-8 h-8 ml-2 border-2 border-emerald-500">
            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs">
              {t('team.you', 'You')}
            </AvatarFallback>
          </Avatar>
        )}
      </div>

      {/* Tags */}
      {task.tags && task.tags.filter(tag => tag !== task.category).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {task.tags.filter(tag => tag !== task.category).map((tag, i) => (
            <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {task.due_date && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {task.due_date}
            </span>
          )}
          {task.attachments?.length > 0 && (
            <span className="flex items-center gap-1">
              <Paperclip className="w-3 h-3" />
              {task.attachments.length}
            </span>
          )}
          <span>{t('tasks.by', 'von')} {task.created_by_name || t('common.unknown', 'Unbekannt')}</span>
        </div>
        
        <div className="flex items-center gap-2">
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="h-7 w-7 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
              title={t('tasks.delete', 'Löschen')}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          {canClaim && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onClaim(); }}
              className="h-7 text-xs"
            >
              {t('tasks.claim', 'Übernehmen')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Quick Documentation Modal - Schnelle Foto-Dokumentation ohne Task-Workflow
const QuickDocModal = ({ projectId, currentUser, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const photoInputRef = useRef(null);

  // Common tags for quick selection
  const commonTags = ['Fortschritt', 'Mängel', 'Material', 'Elektrik', 'Sanitär', 'Rohbau', 'Fertigstellung'];

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Auto-transcribe with user's language
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'voice.webm');
          formData.append('language', currentUser?.language || 'de');
          
          const response = await fetch(`${getApiUrl()}/transcribe-audio`, {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.text) {
              setDescription(prev => prev ? `${prev}\n\n${data.text}` : data.text);
            }
          } else {
            console.error('Transcription failed:', response.status);
          }
        } catch (error) {
          console.error('Transcription error:', error);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      alert(t('alerts.microphoneAccessError'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos(prev => [...prev, {
          file: file,
          preview: event.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const toggleTag = (tag) => {
    setTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const addCustomTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleSubmit = async () => {
    if (photos.length === 0 && !description.trim()) {
      alert(t('alerts.addPhotoOrDescription'));
      return;
    }

    setLoading(true);
    try {
      // Create documentation entry as a special task with status "documented"
      const locale = currentUser?.language || 'de';
      const title = `${t('tasks.documentation', 'Documentation')}: ${tags.length > 0 ? tags[0] : t('common.general', 'General')} - ${new Date().toLocaleDateString(locale)}`;
      
      const response = await fetch(`${getApiUrl()}/tasks?created_by=${currentUser.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          title: title,
          description: description || (photos.length > 0 ? t('tasks.photoDocumentation', 'Photo documentation') : t('tasks.documentation', 'Documentation')),
          priority: 'normal',
          tags: tags,
          is_documentation: true // Flag for documentation entries
        })
      });

      const data = await response.json();
      
      if (data.id) {
        // Upload all photos
        for (const photo of photos) {
          const formData = new FormData();
          formData.append('file', photo.file);
          formData.append('uploader_id', currentUser.id);
          formData.append('attachment_type', 'task');
          
          await fetch(`${getApiUrl()}/tasks/${data.id}/attachments`, {
            method: 'POST',
            body: formData
          });
        }

        // Mark as documented (completed without workflow)
        await fetch(`${getApiUrl()}/tasks/${data.id}/status?status=documented&user_id=${currentUser.id}`, {
          method: 'PUT'
        });
      }

      onCreated();
    } catch (error) {
      alert(t('alerts.docCreateError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            📸 {t('tasks.quickDoc', 'Quick Documentation')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Photo Upload - Optional */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('tasks.takePhotosOptional', 'Take photos (optional)')}
            </label>
            
            {/* Photo Previews */}
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {photos.map((photo, index) => (
                  <div key={index} className="relative">
                    <img 
                      src={photo.preview} 
                      alt={photo.name}
                      className="w-24 h-24 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <input
              type="file"
              ref={photoInputRef}
              onChange={handlePhotoSelect}
              accept="image/*"
              multiple
              capture="environment"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => photoInputRef.current?.click()}
              className="w-full h-20 border-dashed border-2 hover:border-teal-400 hover:bg-teal-50"
            >
              <div className="flex flex-col items-center">
                <Image className="w-8 h-8 mb-1 text-teal-600" />
                <span className="text-teal-700">
                  {photos.length > 0 ? t('tasks.addMorePhotos', 'Add more photos') : t('tasks.takeOrSelectPhotos', 'Take or select photos')}
                </span>
              </div>
            </Button>
          </div>

          {/* Description with Voice */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('tasks.description', 'Description')}
            </label>
            
            {/* Voice Recording */}
            <div className="flex gap-2 mb-2">
              {!isRecording ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={startRecording}
                  disabled={isTranscribing}
                  className="flex-1"
                >
                  <Mic className="w-4 h-4 mr-2" />
                  {isTranscribing ? t('tasks.transcribing', 'Transcribing...') : t('tasks.speakNow', 'Speak now')}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={stopRecording}
                  className="flex-1 bg-red-500 hover:bg-red-600 animate-pulse"
                >
                  <MicOff className="w-4 h-4 mr-2" />
                  {t('tasks.stopRecording', 'Stop recording')}
                </Button>
              )}
            </div>
            
            {isTranscribing && (
              <div className="flex items-center gap-2 text-sm text-teal-600 mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                {t('tasks.convertingAudioToText', 'Converting audio to text...')}
              </div>
            )}
            
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('tasks.descriptionPlaceholder', 'What is being documented? (optional - voice input possible)')}
              className="w-full p-3 border rounded-lg h-24 resize-none"
            />
          </div>

          {/* Quick Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('tasks.category', 'Category')}
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {commonTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    tags.includes(tag)
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder={t('tasks.customCategory', 'Custom category...')}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTag())}
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={addCustomTag}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || (photos.length === 0 && !description.trim()) || isRecording || isTranscribing}
            className="flex-1 bg-teal-600 hover:bg-teal-700"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.saving', 'Saving...')}
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {t('tasks.document', 'Document')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Create Task Modal
const CreateTaskModal = ({ projectId, currentUser, projectMembers, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [assignedTo, setAssignedTo] = useState('');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [pasteNotification, setPasteNotification] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  // Handle paste from clipboard for task attachments
  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            const extension = item.type.split('/')[1] || 'png';
            const fileName = `screenshot_${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`;
            const file = new File([blob], fileName, { type: item.type });
            
            // Show notification
            setPasteNotification(true);
            setTimeout(() => setPasteNotification(false), 2000);
            
            // Add to attachments with preview
            const reader = new FileReader();
            reader.onload = (event) => {
              setAttachments(prev => [...prev, {
                file: file,
                preview: event.target.result,
                name: fileName
              }]);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAttachments(prev => [...prev, {
          file: file,
          preview: file.type.startsWith('image/') ? event.target.result : null,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        
        // Automatically transcribe the audio
        await transcribeAudio(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      alert(t('alerts.microphoneAccessError'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'voice.webm');
      formData.append('language', currentUser?.language || 'de');
      
      const response = await fetch(`${getApiUrl()}/transcribe-audio`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.text) {
          // Append transcribed text to description
          setDescription(prev => {
            const newText = prev ? `${prev}\n\n${data.text}` : data.text;
            return newText;
          });
        }
      }
    } catch (error) {
      console.error('Transcription error:', error);
    } finally {
      setIsTranscribing(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      alert(t('alerts.enterTitle'));
      return;
    }

    setLoading(true);
    try {
      // Create task
      const response = await fetch(`${getApiUrl()}/tasks?created_by=${currentUser.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          title: title.trim(),
          description: description.trim() || null,
          priority,
          assigned_to: assignedTo || null,
          tags,
          due_date: dueDate || null
        })
      });

      const data = await response.json();
      
      // Upload attachments (screenshots, photos)
      if (attachments.length > 0 && data.id) {
        for (const attachment of attachments) {
          const formData = new FormData();
          formData.append('file', attachment.file);
          formData.append('uploader_id', currentUser.id);
          formData.append('attachment_type', 'task');
          
          await fetch(`${getApiUrl()}/tasks/${data.id}/attachments`, {
            method: 'POST',
            body: formData
          });
        }
      }
      
      // Upload voice in background (non-blocking) if recorded
      // The transcribed text is already in the description, so this is optional backup
      if (audioBlob && data.id) {
        const formData = new FormData();
        formData.append('file', audioBlob, 'voice.webm');
        formData.append('uploader_id', currentUser.id);
        
        // Fire and forget - don't wait for upload to complete
        fetch(`${getApiUrl()}/tasks/${data.id}/voice`, {
          method: 'POST',
          body: formData
        }).catch(err => console.log('Voice upload failed (non-critical):', err));
      }

      // Close modal immediately after task creation
      onCreated();
    } catch (error) {
      alert(t('alerts.taskCreateError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">{t('tasks.newTask', 'New Task')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('tasks.taskName', 'Title')} *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('tasks.titlePlaceholder', 'What needs to be done?')}
              className="w-full"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('tasks.description', 'Description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('tasks.descriptionDetailPlaceholder', 'More details...')}
              className="w-full p-2 border rounded-lg resize-none h-24"
            />
          </div>

          {/* Voice Recording */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('tasks.voiceDescription', 'Sprachbeschreibung')}</label>
            <div className="flex items-center gap-2 flex-wrap">
              {!isRecording ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={startRecording}
                  disabled={isTranscribing}
                  className="flex items-center gap-2"
                >
                  <Mic className="w-4 h-4" />
                  {t('tasks.record', 'Aufnehmen')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={stopRecording}
                  className="flex items-center gap-2 animate-pulse"
                >
                  <MicOff className="w-4 h-4" />
                  {t('tasks.stop', 'Stopp')}
                </Button>
              )}
              {isTranscribing && (
                <span className="text-sm text-blue-600 flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  {t('tasks.transcribing', 'Wird transkribiert...')}
                </span>
              )}
              {audioBlob && !isTranscribing && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <Check className="w-4 h-4" /> {t('tasks.transcribed', 'In Beschreibung eingefügt')}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('tasks.voiceHint', 'Sprachaufnahme wird automatisch transkribiert und in die Beschreibung eingefügt')}
            </p>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('tasks.priority', 'Priority')}</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPriority('normal')}
                className={`px-4 py-2 rounded-lg text-sm flex-1 ${
                  priority === 'normal' 
                    ? 'bg-gray-200 text-gray-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {t('tasks.medium')}
              </button>
              <button
                type="button"
                onClick={() => setPriority('urgent')}
                className={`px-4 py-2 rounded-lg text-sm flex-1 ${
                  priority === 'urgent' 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                ⚠️ {t('tasks.high')}
              </button>
            </div>
          </div>

          {/* Assignment */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('tasks.assignTo', 'Assign to')}</label>
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="w-full p-2 border rounded-lg"
            >
              <option value="">{t('tasks.offerToAll', 'Offer to all')}</option>
              {projectMembers.map(member => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('tasks.dueDate', 'Due date')}</label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('tasks.categoryTags', 'Categories/Tags')}</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder={t('tasks.tagPlaceholder', 'e.g. Electric, Plumbing...')}
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              />
              <Button type="button" variant="outline" onClick={addTag}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {tags.map((tag, i) => (
                <span 
                  key={i} 
                  className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-sm flex items-center gap-1"
                >
                  {tag}
                  <button onClick={() => setTags(tags.filter((_, j) => j !== i))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Attachments Section */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('tasks.attachments', 'Attachments')}</label>
            
            {/* Attachment Preview */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {attachments.map((att, index) => (
                  <div key={index} className="relative group">
                    {att.preview ? (
                      <img src={att.preview} alt={att.name} className="w-16 h-16 object-cover rounded-lg border" />
                    ) : (
                      <div className="w-16 h-16 bg-gray-100 rounded-lg border flex items-center justify-center">
                        <FileText className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
              multiple
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-dashed"
            >
              <Image className="w-4 h-4 mr-2" />
              {attachments.length > 0 ? t('tasks.addMoreFiles', 'Add more files') : t('tasks.addFiles', 'Add files')}
            </Button>
            <p className="text-xs text-blue-500 text-center mt-1">
              💡 {t('tasks.pasteHint', 'Ctrl+V / Cmd+V zum Einfügen von Screenshots')}
            </p>
            
            {/* Paste Notification */}
            {pasteNotification && (
              <div className="mt-2 bg-emerald-100 text-emerald-700 px-3 py-2 rounded-lg text-sm text-center">
                📋 {t('tasks.pasteSuccess', 'Screenshot aus Zwischenablage hinzugefügt!')}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !title.trim()}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? t('tasks.creating', 'Creating...') : t('tasks.createTask', 'Create task')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Task Detail Modal - Redesigned with clear sections and step-by-step completion wizard
const TaskDetailModal = ({ task, currentUser, projectMembers, onClose, onUpdate, onDelete }) => {
  const { t } = useTranslation();
  
  // Completion wizard state
  const [completionStep, setCompletionStep] = useState(0); // 0=not started, 1=photo, 2=description, 3=confirm
  const [completionDesc, setCompletionDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [completionPhotos, setCompletionPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [pasteNotification, setPasteNotification] = useState(false);
  const fileInputRef = useRef(null);
  const completionPhotoRef = useRef(null);
  
  // Voice recording state for completion
  const [isRecordingCompletion, setIsRecordingCompletion] = useState(false);
  const [completionAudioBlob, setCompletionAudioBlob] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const completionMediaRecorderRef = useRef(null);
  const completionChunksRef = useRef([]);
  
  // Translation state
  const [translatedTitle, setTranslatedTitle] = useState(null);
  const [translatedDesc, setTranslatedDesc] = useState(null);
  const [translatedVoiceTranscript, setTranslatedVoiceTranscript] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [editDesc, setEditDesc] = useState(task.description || '');
  const [editPriority, setEditPriority] = useState(task.priority || 'normal');
  const [editAssignedTo, setEditAssignedTo] = useState(task.assigned_to || '');
  const [editDueDate, setEditDueDate] = useState(task.due_date || '');
  const [savingEdit, setSavingEdit] = useState(false);

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) return;
    setSavingEdit(true);
    try {
      const payload = {
        title: editTitle,
        description: editDesc,
        priority: editPriority,
        assigned_to: editAssignedTo || '',
        due_date: editDueDate || ''
      };
      if (editAssignedTo && task.status === 'open') payload.status = 'assigned';
      if (!editAssignedTo && task.status === 'assigned') payload.status = 'open';
      const response = await fetch(`${getApiUrl()}/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Update failed');
      onUpdate();
    } catch (error) {
      alert(t('tasks.updateError', 'Task konnte nicht aktualisiert werden'));
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle paste from clipboard for completion photos
  useEffect(() => {
    const handlePaste = async (e) => {
      // Only process if we're in photo step
      if (completionStep !== 1) return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            const extension = item.type.split('/')[1] || 'png';
            const fileName = `screenshot_${new Date().toISOString().replace(/[:.]/g, '-')}.${extension}`;
            const file = new File([blob], fileName, { type: item.type });
            
            setPasteNotification(true);
            setTimeout(() => setPasteNotification(false), 2000);
            
            const reader = new FileReader();
            reader.onload = (event) => {
              setCompletionPhotos(prev => [...prev, {
                file: file,
                preview: event.target.result,
                name: fileName
              }]);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [completionStep]);

  const isWorker = task.assigned_to === currentUser.id || task.claimed_by === currentUser.id;
  const isCreator = task.created_by === currentUser.id;
  const canComplete = isWorker && ['assigned', 'in_progress'].includes(task.status);
  const canReview = isCreator && task.status === 'review';
  
  // Check if translation might be needed (user's language differs from likely task language)
  const userLang = currentUser?.language || 'de';
  const needsTranslation = userLang !== 'de'; // Tasks are typically created in German

  // Translate task content on demand
  const translateTask = async () => {
    if (isTranslating) return;
    
    setIsTranslating(true);
    try {
      // Include voice_transcript if available
      const textsToTranslate = [task.title, task.description || ''];
      if (task.voice_transcript) {
        textsToTranslate.push(task.voice_transcript);
      }
      
      const response = await fetch(`${getApiUrl()}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          texts: textsToTranslate,
          target_language: userLang
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.translations && result.translations.length >= 2) {
          setTranslatedTitle(result.translations[0]);
          setTranslatedDesc(result.translations[1]);
          if (result.translations[2]) {
            setTranslatedVoiceTranscript(result.translations[2]);
          }
          setShowTranslation(true);
        }
      } else {
        console.error('Translation failed:', response.status);
        alert(t('tasks.translationFailed', 'Übersetzung fehlgeschlagen'));
      }
    } catch (error) {
      console.error('Translation error:', error);
      alert(t('tasks.translationFailed', 'Übersetzung fehlgeschlagen') + ': ' + error.message);
    } finally {
      setIsTranslating(false);
    }
  };

  // Voice recording functions for completion
  const startCompletionRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      completionMediaRecorderRef.current = mediaRecorder;
      completionChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) completionChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(completionChunksRef.current, { type: 'audio/webm' });
        setCompletionAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        
        // Auto-transcribe the audio with user's language
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'completion_voice.webm');
          formData.append('language', currentUser?.language || 'de');
          
          const response = await fetch(`${getApiUrl()}/transcribe-audio`, {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.text) {
              setCompletionDesc(prev => prev ? `${prev}\n\n${data.text}` : data.text);
            }
          } else {
            console.error('Transcription failed:', response.status);
          }
        } catch (error) {
          console.error('Transcription error:', error);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.start();
      setIsRecordingCompletion(true);
    } catch (error) {
      alert(t('alerts.microphoneAccessError'));
    }
  };

  const stopCompletionRecording = () => {
    if (completionMediaRecorderRef.current) {
      completionMediaRecorderRef.current.stop();
      setIsRecordingCompletion(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await fetch(`${getApiUrl()}/tasks/${task.id}/status?status=${newStatus}&user_id=${currentUser.id}`, {
        method: 'PUT'
      });
      onUpdate();
    } catch (error) {
      alert(t('alerts.statusChangeError'));
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // First upload completion photos if any
      const photoUrls = [];
      for (const photo of completionPhotos) {
        const formData = new FormData();
        formData.append('file', photo.file);
        formData.append('uploader_id', currentUser.id);
        
        const uploadRes = await fetch(`${getApiUrl()}/files/upload/${task.project_id}`, {
          method: 'POST',
          body: formData
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          if (uploadData.file_path) {
            photoUrls.push(uploadData.file_path);
          }
        }
      }
      
      // Complete task with proof
      const completeUrl = new URL(`${getApiUrl()}/tasks/${task.id}/complete`);
      completeUrl.searchParams.append('user_id', currentUser.id);
      completeUrl.searchParams.append('description', completionDesc);
      
      await fetch(completeUrl.toString(), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          photos: photoUrls,
          description: completionDesc
        })
      });
      
      // Reset wizard state
      setCompletionStep(0);
      setCompletionPhotos([]);
      setCompletionDesc('');
      setCompletionAudioBlob(null);
      
      onUpdate();
    } catch (error) {
      console.error('Complete error:', error);
      alert(t('alerts.completeError', 'Fehler beim Abschließen'));
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (approved) => {
    setLoading(true);
    try {
      await fetch(`${getApiUrl()}/tasks/${task.id}/review?user_id=${currentUser.id}&approved=${approved}`, {
        method: 'PUT'
      });
      onUpdate();
    } catch (error) {
      alert(t('alerts.reviewError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, type = 'task') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploader_id', currentUser.id);
    formData.append('attachment_type', type);

    try {
      await fetch(`${getApiUrl()}/tasks/${task.id}/attachments`, {
        method: 'POST',
        body: formData
      });
      onUpdate();
    } catch (error) {
      alert(t('alerts.uploadError'));
    }
  };

  // Handle completion photo upload (with preview before submit)
  const handleCompletionPhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCompletionPhotos(prev => [...prev, {
          file: file,
          preview: event.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input
    if (completionPhotoRef.current) {
      completionPhotoRef.current.value = '';
    }
  };

  const removeCompletionPhoto = (index) => {
    setCompletionPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Enhanced complete handler with photo upload
  const handleCompleteWithPhotos = async () => {
    setLoading(true);
    try {
      // First upload all photos
      for (const photo of completionPhotos) {
        const formData = new FormData();
        formData.append('file', photo.file);
        formData.append('uploader_id', currentUser.id);
        formData.append('attachment_type', 'completion');
        
        await fetch(`${getApiUrl()}/tasks/${task.id}/attachments`, {
          method: 'POST',
          body: formData
        });
      }

      // Then complete the task
      await fetch(`${getApiUrl()}/tasks/${task.id}/complete?user_id=${currentUser.id}&description=${encodeURIComponent(completionDesc)}`, {
        method: 'PUT'
      });
      
      onUpdate();
    } catch (error) {
      alert(t('alerts.completeError'));
    } finally {
      setLoading(false);
    }
  };

  // Helper to format dates nicely
  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleString(currentUser?.language || 'de', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[task.status]}`}>
              {getStatusLabel(task.status, t)}
            </span>
            {task.priority === 'urgent' && (
              <span className="text-red-600 text-sm">⚠️ {t('tasks.high')}</span>
            )}
            {(task.is_bedarfsposition || task.tags?.includes('Bedarfsposition')) && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-200 text-orange-800">⚡ Bedarf</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {isCreator && !isEditing && (
              <button onClick={() => setIsEditing(true)} className="p-2 hover:bg-gray-100 rounded-lg" title={t('tasks.editTask', 'Task bearbeiten')} data-testid="task-edit-btn">
                <Edit2 className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="task-modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* ========== SECTION 1: AUFGABENSTELLUNG (Assignment) ========== */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <h3 className="font-semibold text-blue-900">{t('tasks.assignment', 'Aufgabenstellung')}</h3>
            </div>
            
            {isEditing ? (
              <div className="space-y-3" data-testid="task-edit-form">
                <div>
                  <label className="text-sm font-medium text-blue-900">{t('tasks.taskName', 'Titel')}</label>
                  <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="bg-white mt-1" data-testid="task-edit-title" />
                </div>
                <div>
                  <label className="text-sm font-medium text-blue-900">{t('tasks.description', 'Beschreibung')}</label>
                  <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} className="w-full p-2 border rounded-lg h-28 text-sm bg-white mt-1" data-testid="task-edit-desc" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium text-blue-900">{t('tasks.priority', 'Priorität')}</label>
                    <select value={editPriority} onChange={(e) => setEditPriority(e.target.value)} className="w-full p-2 border rounded-lg text-sm bg-white mt-1" data-testid="task-edit-priority">
                      <option value="normal">{t('tasks.normal', 'Normal')}</option>
                      <option value="urgent">{t('tasks.urgent', 'Dringend')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-900">{t('tasks.dueDate', 'Fällig am')}</label>
                    <input type="date" value={editDueDate} onChange={(e) => setEditDueDate(e.target.value)} className="w-full p-2 border rounded-lg text-sm bg-white mt-1" data-testid="task-edit-duedate" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-blue-900">{t('tasks.assignTo', 'Zuweisen an')}</label>
                  <select value={editAssignedTo} onChange={(e) => setEditAssignedTo(e.target.value)} className="w-full p-2 border rounded-lg text-sm bg-white mt-1" data-testid="task-edit-assignee">
                    <option value="">{t('tasks.unassigned', 'Nicht zugewiesen (offen für alle)')}</option>
                    {projectMembers.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" onClick={() => setIsEditing(false)} className="flex-1" disabled={savingEdit}>
                    {t('common.cancel', 'Abbrechen')}
                  </Button>
                  <Button onClick={handleSaveEdit} disabled={savingEdit || !editTitle.trim()} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="task-edit-save">
                    {savingEdit ? t('common.saving', 'Speichern...') : t('common.save', 'Speichern')}
                  </Button>
                </div>
              </div>
            ) : (
            <>
            {/* Title */}
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {showTranslation && translatedTitle ? translatedTitle : task.title}
            </h2>
            {showTranslation && translatedTitle && (
              <p className="text-xs text-gray-400 mb-2">Original: {task.title}</p>
            )}
            
            {/* Description */}
            {task.description && (
              <p className="text-gray-700 mb-3">
                {showTranslation && translatedDesc ? translatedDesc : task.description}
              </p>
            )}
            
            {/* Voice transcript */}
            {task.voice_transcript && (
              <div className="bg-white/70 p-3 rounded-lg mb-3">
                <p className="text-sm text-blue-800">
                  <Mic className="w-4 h-4 inline mr-1" />
                  {t('tasks.voiceNote', 'Sprachnotiz')}: {showTranslation && translatedVoiceTranscript ? translatedVoiceTranscript : task.voice_transcript}
                </p>
              </div>
            )}
            
            {/* Translate button */}
            {needsTranslation && !showTranslation && (
              <button
                onClick={translateTask}
                disabled={isTranslating}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50 mb-3"
              >
                {isTranslating ? <><span className="animate-spin">⏳</span> {t('tasks.translating', 'Übersetze...')}</> : 
                  <>🌐 {t('tasks.translateToMyLanguage', 'In meine Sprache übersetzen')}</>}
              </button>
            )}
            {showTranslation && (
              <button onClick={() => setShowTranslation(false)} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white text-gray-600 rounded-lg hover:bg-gray-100 mb-3">
                📝 {t('tasks.showOriginal', 'Original anzeigen')}
              </button>
            )}
            
            {/* Tags */}
            {task.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {task.tags.map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-white/70 text-blue-800 rounded text-xs">{tag}</span>
                ))}
              </div>
            )}
            
            {/* Task Attachments */}
            {task.attachments?.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-blue-800 mb-2">{t('tasks.attachments', 'Anhänge')} ({task.attachments.length})</p>
                <div className="grid grid-cols-3 gap-2">
                  {task.attachments.map((att, i) => {
                    const isImage = att.file_type === 'image' || att.file_path?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                    const fileUrl = getFullFileUrl(att.file_path);
                    return (
                      <a key={i} href={fileUrl} target="_blank" rel="noopener noreferrer" className="block bg-white rounded-lg overflow-hidden hover:ring-2 ring-blue-300">
                        {isImage ? (
                          <img src={fileUrl} alt={att.file_name || 'Anhang'} className="w-full h-16 object-cover" />
                        ) : (
                          <div className="flex items-center justify-center h-16 bg-gray-50">
                            <FileText className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Timestamp & Creator Info */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-blue-700 border-t border-blue-200 pt-3 mt-3">
              <span>📅 {t('tasks.createdAt', 'Erstellt')}: {formatDateTime(task.created_at)}</span>
              <span>👤 {t('tasks.by', 'von')}: {task.created_by_name}</span>
              {task.due_date && <span>⏰ {t('tasks.due', 'Fällig')}: {task.due_date}</span>}
            </div>
            </>
            )}
          </div>

          {/* ========== SECTION 2: BEARBEITUNG (In Progress) ========== */}
          {(task.assigned_to || task.claimed_by || task.claimed_at || task.status === 'in_progress') && (
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-semibold text-yellow-900">{t('tasks.inProgressSection', 'Bearbeitung')}</h3>
              </div>
              
              <div className="space-y-2 text-sm text-yellow-800">
                {(task.assigned_to_name || task.claimed_by_name) && (
                  <p>👷 {t('tasks.worker', 'Bearbeiter')}: <strong>{task.claimed_by_name || task.assigned_to_name}</strong></p>
                )}
                {task.claimed_at && (
                  <p>📅 {t('tasks.claimedAt', 'Übernommen am')}: {formatDateTime(task.claimed_at)}</p>
                )}
                {task.started_at && (
                  <p>🚀 {t('tasks.startedAt', 'Gestartet am')}: {formatDateTime(task.started_at)}</p>
                )}
              </div>
            </div>
          )}

          {/* ========== SECTION 3: ABSCHLUSS-DOKUMENTATION (Completion) ========== */}
          {(task.status === 'completed' || task.status === 'review' || task.completion_proof) && (
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-semibold text-green-900">{t('tasks.completionSection', 'Abschluss-Dokumentation')}</h3>
              </div>
              
              {task.completion_proof?.description && (
                <p className="text-green-800 mb-3">{task.completion_proof.description}</p>
              )}
              
              {task.completion_proof?.photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {task.completion_proof.photos.map((photo, i) => (
                    <a key={i} href={getFullFileUrl(photo)} target="_blank" rel="noopener noreferrer">
                      <img src={getFullFileUrl(photo)} alt={t('tasks.proof', 'Nachweis')} className="w-full h-20 object-cover rounded-lg border border-green-300" />
                    </a>
                  ))}
                </div>
              )}
              
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-green-700 border-t border-green-200 pt-3">
                {task.completed_at && <span>✅ {t('tasks.completedAt', 'Abgeschlossen am')}: {formatDateTime(task.completed_at)}</span>}
                {task.completed_by_name && <span>👤 {t('tasks.by', 'von')}: {task.completed_by_name}</span>}
              </div>
            </div>
          )}

          {/* Add Attachment Button (only for ongoing tasks) */}
          {(isWorker || isCreator) && !['completed', 'review'].includes(task.status) && (
            <div>
              <input type="file" ref={fileInputRef} onChange={(e) => handleFileUpload(e)} className="hidden" accept="image/*,.pdf,.doc,.docx" />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
                <Paperclip className="w-4 h-4 mr-2" />
                {t('tasks.addAttachment', 'Anhang hinzufügen')}
              </Button>
            </div>
          )}
        </div>

        {/* ========== ACTIONS FOOTER ========== */}
        <div className="p-4 border-t space-y-2 bg-gray-50">
          {/* Worker Actions - Start completion wizard */}
          {canComplete && completionStep === 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleStatusChange('in_progress')}
                className="flex-1"
                disabled={task.status === 'in_progress'}
              >
                <Clock className="w-4 h-4 mr-1" />
                {t('tasks.inProgress', 'In Bearbeitung')}
              </Button>
              <Button
                onClick={() => setCompletionStep(1)}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {t('tasks.complete', 'Abschließen')}
              </Button>
            </div>
          )}

          {/* ===== COMPLETION WIZARD ===== */}
          {completionStep > 0 && (
            <div className="bg-white rounded-xl p-4 border-2 border-emerald-300">
              {/* Progress indicator */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${completionStep >= 1 ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
                <div className={`w-8 h-0.5 ${completionStep >= 2 ? 'bg-emerald-500' : 'bg-gray-200'}`}></div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${completionStep >= 2 ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
                <div className={`w-8 h-0.5 ${completionStep >= 3 ? 'bg-emerald-500' : 'bg-gray-200'}`}></div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${completionStep >= 3 ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'}`}>3</div>
              </div>

              {/* Step 1: Photos */}
              {completionStep === 1 && (
                <div className="space-y-3">
                  <div className="text-center">
                    <Camera className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-lg">{t('tasks.step1Title', 'Schritt 1: Foto-Nachweis')}</h4>
                    <p className="text-sm text-gray-500">{t('tasks.step1Desc', 'Fügen Sie Fotos als Nachweis der erledigten Arbeit hinzu (optional)')}</p>
                  </div>
                  
                  {/* Photo previews */}
                  {completionPhotos.length > 0 && (
                    <div className="flex flex-wrap gap-2 justify-center">
                      {completionPhotos.map((photo, index) => (
                        <div key={index} className="relative">
                          <img src={photo.preview} alt={photo.name} className="w-20 h-20 object-cover rounded-lg border" />
                          <button type="button" onClick={() => removeCompletionPhoto(index)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <input type="file" ref={completionPhotoRef} onChange={handleCompletionPhotoSelect} accept="image/*" multiple className="hidden" />
                  
                  <div className="space-y-2">
                    <Button type="button" variant="outline" onClick={() => completionPhotoRef.current?.click()} className="w-full border-2 border-dashed border-emerald-300 py-6">
                      <Camera className="w-5 h-5 mr-2" />
                      {completionPhotos.length > 0 ? t('tasks.addMorePhotos', 'Weitere Fotos') : t('tasks.takeOrSelectPhoto', 'Foto aufnehmen / auswählen')}
                    </Button>
                    <p className="text-xs text-center text-blue-500">💡 Ctrl+V zum Einfügen von Screenshots</p>
                  </div>
                  
                  {pasteNotification && (
                    <div className="bg-emerald-100 text-emerald-700 px-3 py-2 rounded-lg text-sm text-center">📋 Screenshot hinzugefügt!</div>
                  )}
                  
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setCompletionStep(0)} className="flex-1">{t('common.cancel', 'Abbrechen')}</Button>
                    <Button onClick={() => setCompletionStep(2)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                      {completionPhotos.length > 0 ? t('common.next', 'Weiter') : t('tasks.skipPhotos', 'Ohne Foto weiter')} →
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Description */}
              {completionStep === 2 && (
                <div className="space-y-3">
                  <div className="text-center">
                    <Edit2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-lg">{t('tasks.step2Title', 'Schritt 2: Beschreibung')}</h4>
                    <p className="text-sm text-gray-500">{t('tasks.step2Desc', 'Was wurde gemacht? Gibt es Anmerkungen?')}</p>
                  </div>
                  
                  {/* Voice Recording */}
                  <div className="flex gap-2">
                    {!isRecordingCompletion ? (
                      <Button type="button" variant="outline" onClick={startCompletionRecording} disabled={isTranscribing} className="flex-1 border-emerald-300 text-emerald-700">
                        <Mic className="w-4 h-4 mr-2" />
                        {isTranscribing ? t('tasks.transcribing', 'Transkribiere...') : t('tasks.speakDescription', 'Einsprechen')}
                      </Button>
                    ) : (
                      <Button type="button" onClick={stopCompletionRecording} className="flex-1 bg-red-500 hover:bg-red-600 text-white animate-pulse">
                        <MicOff className="w-4 h-4 mr-2" />
                        {t('tasks.stopRecording', 'Stopp')}
                      </Button>
                    )}
                  </div>
                  
                  {isTranscribing && (
                    <div className="flex items-center justify-center gap-2 text-sm text-emerald-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                      {t('tasks.convertingAudioToText', 'Audio wird transkribiert...')}
                    </div>
                  )}
                  
                  <textarea
                    value={completionDesc}
                    onChange={(e) => setCompletionDesc(e.target.value)}
                    placeholder={t('tasks.completionPlaceholder', 'Beschreiben Sie kurz, was erledigt wurde...')}
                    className="w-full p-3 border-2 rounded-lg h-32 resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setCompletionStep(1)} className="flex-1">← {t('common.back', 'Zurück')}</Button>
                    <Button onClick={() => setCompletionStep(3)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                      {t('common.next', 'Weiter')} →
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Confirm */}
              {completionStep === 3 && (
                <div className="space-y-3">
                  <div className="text-center">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                    <h4 className="font-semibold text-lg">{t('tasks.step3Title', 'Schritt 3: Bestätigen')}</h4>
                    <p className="text-sm text-gray-500">{t('tasks.step3Desc', 'Überprüfen Sie Ihre Eingaben')}</p>
                  </div>
                  
                  {/* Summary */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">📷</span>
                      <div>
                        <p className="text-sm font-medium">{t('tasks.photos', 'Fotos')}</p>
                        <p className="text-xs text-gray-500">
                          {completionPhotos.length > 0 ? `${completionPhotos.length} ${t('tasks.photosAdded', 'Foto(s) hinzugefügt')}` : t('tasks.noPhotos', 'Keine Fotos')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-lg">📝</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{t('tasks.description', 'Beschreibung')}</p>
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {completionDesc || t('tasks.noDescription', 'Keine Beschreibung')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setCompletionStep(2)} className="flex-1">← {t('common.back', 'Zurück')}</Button>
                    <Button onClick={handleComplete} disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                      {loading ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> {t('tasks.completing', 'Wird abgeschlossen...')}</>
                      ) : (
                        <><Check className="w-4 h-4 mr-1" /> {t('tasks.confirmComplete', 'Task abschließen')}</>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Review Actions */}
          {canReview && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleReview(false)}
                className="flex-1 text-red-600 border-red-200"
                disabled={loading}
              >
                <XCircle className="w-4 h-4 mr-1" />
                {t('tasks.reject', 'Ablehnen')}
              </Button>
              <Button
                onClick={() => handleReview(true)}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Genehmigen
              </Button>
            </div>
          )}

          {/* Delete Button - Only for creator */}
          {isCreator && (
            <Button
              variant="outline"
              onClick={() => {
                if (window.confirm(t('tasks.confirmDelete', 'Möchten Sie diesen Task wirklich löschen?'))) {
                  onDelete();
                }
              }}
              className="w-full text-red-600 hover:bg-red-50 hover:border-red-300"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('tasks.deleteTask', 'Task löschen')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Notification Bell Component
export const NotificationBell = ({ userId }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [userId]);

  const loadNotifications = async () => {
    try {
      const response = await fetchWithRetry(`${getApiUrl()}/notifications/${userId}`);
      if (!response.ok) {
        // Stiller Fehler bei temporären Server-Problemen
        if (![500, 502, 520].includes(response.status)) {
          console.warn('Notifications endpoint returned', response.status);
        }
        return;
      }
      const data = await response.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      // Stille Fehler - Notifications sind nicht kritisch
    }
  };

  const markAllRead = async () => {
    try {
      await fetchWithRetry(`${getApiUrl()}/notifications/${userId}/read-all`, { method: 'PUT' });
      loadNotifications();
    } catch (error) {
      // Stille Fehler bei temporären Server-Problemen
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 hover:bg-gray-100 rounded-lg"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50">
          <div className="p-3 border-b flex items-center justify-between">
            <h3 className="font-semibold">{t('notifications.title', 'Notifications')}</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-emerald-600">
                {t('notifications.markAllRead', 'Mark all read')}
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="p-4 text-center text-gray-500 text-sm">{t('notifications.noNotifications', 'No notifications')}</p>
            ) : (
              notifications.slice(0, 10).map(n => (
                <div 
                  key={n.id} 
                  className={`p-3 border-b hover:bg-gray-50 ${!n.read ? 'bg-blue-50' : ''}`}
                >
                  <p className="font-medium text-sm">{n.title}</p>
                  <p className="text-xs text-gray-600">{n.message}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(n.created_at).toLocaleString('de')}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Report Generator Component
export const ReportGenerator = ({ projectId, projectName }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [generating, setGenerating] = useState(false);

  const generateReport = async () => {
    setGenerating(true);
    try {
      let url = `${getApiUrl()}/reports/generate/${projectId}`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += '?' + params.toString();

      const response = await fetch(url);
      if (!response.ok) throw new Error('Report generation failed');

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `Projektdokumentation_${projectName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      alert(t('alerts.reportError'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <FileText className="w-5 h-5" />
        {t('reports.createProjectDocumentation', 'Create project documentation')}
      </h3>
      
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('reports.from', 'From')}</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('reports.to', 'To')}</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        
        <Button
          onClick={generateReport}
          disabled={generating}
          className="w-full bg-emerald-600 hover:bg-emerald-700"
        >
          {generating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('reports.generating', 'Generating...')}
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              {t('reports.downloadDocx', 'Download DOCX report')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Inline Report Generator (compact version for task list header)
const ReportGeneratorInline = ({ projectId, projectName }) => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatingExcel, setGeneratingExcel] = useState(false);

  const generateReport = async (format = 'docx') => {
    const isExcel = format === 'excel';
    if (isExcel) {
      setGeneratingExcel(true);
    } else {
      setGenerating(true);
    }
    
    try {
      const endpoint = isExcel ? 'generate-excel' : 'generate';
      let url = `${getApiUrl()}/reports/${endpoint}/${projectId}`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += '?' + params.toString();

      const response = await fetch(url);
      if (!response.ok) throw new Error('Report generation failed');

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const extension = isExcel ? 'xlsx' : 'docx';
      a.download = `Projektdokumentation_${(projectName || 'Projekt').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      alert(t('reports.error') || 'Fehler beim Generieren des Reports');
    } finally {
      setGenerating(false);
      setGeneratingExcel(false);
    }
  };

  return (
    <div className="p-3 flex items-center gap-3 flex-wrap">
      <span className="text-sm font-medium text-gray-700">📄 {t('reports.title') || 'Projektdokumentation'}:</span>
      <div className="flex items-center gap-2">
        <Input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="h-8 w-36 text-sm"
          placeholder={t('tasks.dateFrom', 'Von')}
        />
        <span className="text-gray-400">–</span>
        <Input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="h-8 w-36 text-sm"
          placeholder={t('tasks.dateTo', 'Bis')}
        />
      </div>
      <div className="flex gap-2">
        <Button
          onClick={() => generateReport('docx')}
          disabled={generating || generatingExcel}
          size="sm"
          className="h-8 bg-emerald-600 hover:bg-emerald-700"
        >
          {generating ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
              ...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-1" />
              DOCX
            </>
          )}
        </Button>
        <Button
          onClick={() => generateReport('excel')}
          disabled={generating || generatingExcel}
          size="sm"
          variant="outline"
          className="h-8 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
        >
          {generatingExcel ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-600 mr-1"></div>
              ...
            </>
          ) : (
            <>
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              Excel
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default TaskList;
